import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { session } from './api';
import { visibleInterval } from './polling';
import { channelsCarrying } from './realtime-channels';

type Listener = (event: string, payload: unknown) => void;

export const REALTIME_PATH = '/realtime';
export const INVALIDATE_DEBOUNCE_MS = 1_500;
const HIDDEN_GRACE_MS = 30_000;

export interface RealtimeTransport {
  connect(onEvent: (channel: string | null, event: string, payload: unknown) => void, onState: (connected: boolean) => void): void;
  subscribe(channels: string[]): void;
  disconnect(): void;
}

function socketTransport(): RealtimeTransport {
  let socket: Socket | null = null;
  return {
    connect(onEvent, onState) {
      socket = io({
        path: REALTIME_PATH,
        transports: ['websocket'],
        auth: (cb) => cb({ token: session.access() ?? '' }),
        reconnectionDelay: 2_000,
        reconnectionDelayMax: 30_000,
      });
      socket.on('ready', () => onState(true));
      socket.on('disconnect', () => onState(false));
      socket.on('connect_error', () => onState(false));
      socket.onAny((event: string, payload: unknown) => {
        if (event !== 'ready') onEvent(null, event, payload);
      });
    },
    subscribe(channels) {
      socket?.emit('subscribe', channels);
    },
    disconnect() {
      socket?.disconnect();
      socket = null;
    },
  };
}

interface Subscription {
  channels: Set<string>;
  events: Set<string> | null;
  listener: Listener;
}

export class RealtimeHub {
  private readonly counts = new Map<string, number>();
  private readonly listeners = new Set<Subscription>();
  private readonly stateListeners = new Set<() => void>();
  private transport: RealtimeTransport | null = null;
  private connected = false;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly makeTransport: () => RealtimeTransport = socketTransport,
    private readonly isVisible: () => boolean = () => typeof document === 'undefined' || document.visibilityState === 'visible',
  ) {}

  get isConnected(): boolean {
    return this.connected;
  }

  get channels(): string[] {
    return [...this.counts.keys()];
  }

  get active(): boolean {
    return this.transport !== null;
  }

  onState(listener: () => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  subscribe(channels: string[], listener: Listener, events: readonly string[] | null = null): () => void {
    const entry: Subscription = { channels: new Set(channels), events: events ? new Set(events) : null, listener };
    this.listeners.add(entry);
    const fresh = channels.filter((channel) => {
      const count = this.counts.get(channel) ?? 0;
      this.counts.set(channel, count + 1);
      return count === 0;
    });
    this.ensureConnected();
    if (fresh.length > 0 && this.connected) this.transport?.subscribe(fresh);
    return () => {
      this.listeners.delete(entry);
      for (const channel of channels) {
        const count = (this.counts.get(channel) ?? 1) - 1;
        if (count <= 0) this.counts.delete(channel);
        else this.counts.set(channel, count);
      }
      if (this.counts.size === 0) this.close();
    };
  }

  visibilityChanged(): void {
    if (this.isVisible()) {
      if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
      this.hiddenTimer = null;
      this.ensureConnected();
      return;
    }
    if (this.hiddenTimer || !this.transport) return;
    this.hiddenTimer = setTimeout(() => {
      this.hiddenTimer = null;
      if (!this.isVisible()) this.close();
    }, HIDDEN_GRACE_MS);
  }

  private ensureConnected(): void {
    if (this.transport || this.counts.size === 0 || !this.isVisible() || !session.access()) return;
    const transport = this.makeTransport();
    this.transport = transport;
    transport.connect(
      (_channel, event, payload) => this.dispatch(event, payload),
      (connected) => {
        this.setConnected(connected);
        if (connected && this.counts.size > 0) transport.subscribe(this.channels);
      },
    );
  }

  private dispatch(event: string, payload: unknown): void {
    const carrying = channelsCarrying(this.channels, event, payload);
    for (const entry of [...this.listeners]) {
      if (entry.events && !entry.events.has(event)) continue;
      if ([...entry.channels].some((channel) => carrying.has(channel))) entry.listener(event, payload);
    }
  }

  private setConnected(connected: boolean): void {
    if (this.connected === connected) return;
    this.connected = connected;
    for (const listener of this.stateListeners) listener();
  }

  private close(): void {
    this.transport?.disconnect();
    this.transport = null;
    this.setConnected(false);
  }
}

export const realtime = new RealtimeHub();

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => realtime.visibilityChanged());
}

export function useRealtimeConnected(hub: RealtimeHub = realtime): boolean {
  return useSyncExternalStore(
    (onChange) => hub.onState(onChange),
    () => hub.isConnected,
    () => false,
  );
}

export function useRealtime(channels: string[], onEvent: Listener, hub: RealtimeHub = realtime, events: readonly string[] | null = null): void {
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const key = [...channels].sort().join('|');
  const eventKey = events ? [...events].sort().join('|') : null;
  useEffect(() => {
    if (!key) return undefined;
    return hub.subscribe(key.split('|'), (event, payload) => handler.current(event, payload), eventKey === null ? null : eventKey.split('|'));
  }, [key, eventKey, hub]);
}

export function useRealtimeInvalidate(channels: string[], events: string[] | null, queryKeys: QueryKey[], hub: RealtimeHub = realtime): boolean {
  const queryClient = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keys = useRef(queryKeys);
  keys.current = queryKeys;
  useRealtime(
    channels,
    () => {
      if (timer.current) return;
      timer.current = setTimeout(() => {
        timer.current = null;
        for (const queryKey of keys.current) void queryClient.invalidateQueries({ queryKey });
      }, INVALIDATE_DEBOUNCE_MS);
    },
    hub,
    events,
  );
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return useRealtimeConnected(hub);
}

export function fallbackInterval(connected: boolean, ms: number): false | (() => number | false) {
  return connected ? false : visibleInterval(ms);
}
